import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthSplitLayout } from '@l2u/ui/components/AuthSplitLayout.tsx';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { ApiError } from '@l2u/ui/lib/api.ts';
import { setAdminAccessToken } from '@l2u/ui/lib/auth/adminToken.ts';
import { useAdminAuth, type AccessCheck } from '../lib/auth/admin.tsx';
import { forgetSsoUser, rememberSsoUser, safeStorage } from '../lib/auth/ssoHint.ts';
import { getAdminSupabase } from '@l2u/ui/lib/supabase.ts';
import { parseAuthErrorFromUrl } from '@l2u/ui/lib/authErrorFromUrl.ts';

// Landing point for the SAML SSO return. The admin Supabase client sets
// detectSessionInUrl: false on purpose (packages/ui/src/lib/supabase.ts) —
// so this route picks the session out of the URL itself:
//   - implicit flow (the admin client's default): #access_token & #refresh_token
//   - PKCE shape (?code=): kept as a fallback; both shapes are observed on
//     this project's auth links (see AdminAcceptInvite).
// After establishing the session it confirms admin membership via
// /api/auth/admin/me so an un-invited SSO user gets a clear message instead
// of a silent sign-out.
export function SsoCallback() {
  const navigate = useNavigate();
  const { checkAccess } = useAdminAuth();
  const [notice, setNotice] = useState<string | null>(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      // IdP / SAML errors ride back on the return URL instead of tokens.
      const authError = parseAuthErrorFromUrl(window.location.search, window.location.hash);
      if (authError) {
        window.history.replaceState(null, '', window.location.pathname);
        setNotice(
          authError.description ??
            'Single sign-on was cancelled or failed. Please try again or sign in another way.',
        );
        return;
      }

      const supabase = getAdminSupabase();
      const hash = window.location.hash.replace(/^#/, '');
      const fragment = hash ? new URLSearchParams(hash) : null;
      const query = new URLSearchParams(window.location.search);
      const access = fragment?.get('access_token');
      const refresh = fragment?.get('refresh_token');
      const code = query.get('code');

      // The session's OWN token: setSession refreshes an expired access token,
      // and checkAccess must use the token auth-js just reported in SIGNED_IN
      // so both share one request.
      let token: string | null;
      if (access && refresh) {
        const { data, error } = await supabase.auth.setSession({
          access_token: access,
          refresh_token: refresh,
        });
        if (error) throw error;
        token = data.session?.access_token ?? null;
      } else if (code) {
        const { data, error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) throw error;
        token = data.session?.access_token ?? null;
      } else {
        setNotice('This sign-in link is missing its credentials. Please try again.');
        return;
      }

      // Strip tokens from the URL before any further navigation or notice.
      window.history.replaceState(null, '', window.location.pathname);

      setAdminAccessToken(token);
      if (!token) {
        setNotice('We could not complete sign-in on this portal. Please try again.');
        return;
      }

      // Confirm this SSO user maps to an admin. On first login the API
      // attempts the guarded email backfill or provisions from the group
      // claim; refusals come back as 403 with a machine reason in `details`.
      // Asked through the provider, which shares the request its SIGNED_IN
      // handler already started for this token instead of sending a second.
      let check: AccessCheck;
      try {
        check = await checkAccess(token);
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          // The session we just established is not usable at all — there is
          // nothing to preserve, so clear it rather than leaving the user half
          // signed in. Deliberately says nothing about authorisation: that
          // wording belongs to AdminNoAccess alone.
          try {
            await supabase.auth.signOut();
          } catch {
            // ignore
          }
          setAdminAccessToken(null);
          setNotice('We could not complete sign-in on this portal. Please try again.');
          return;
        }
        throw err;
      }

      // 403: authenticated but refused. The session is deliberately KEPT —
      // clearing it here would end only THIS portal's session while the
      // central l2u-identity and Okta sessions survive, so the next sign-in
      // click would round-trip silently and refuse again (spec S10). Hand off
      // to /no-access, which owns the wording and offers a sign-out that ends
      // the central session too. The reason rides in history state so that
      // page's first paint is already correct.
      if (check.status === 'denied') {
        // A definite refusal also DISARMS automatic re-entry, matching billing.
        // /no-access does offer a hint-clearing sign-out, but relying on the
        // user finding it means every new tab meanwhile takes a silent round
        // trip into the same refusal. Only this branch clears: the 401 path
        // above is "session unusable", not a decision about this person.
        forgetSsoUser(safeStorage(() => window.localStorage));
        navigate('/no-access', { replace: true, state: { reason: check.reason } });
        return;
      }

      // The ONLY place the hint is recorded, and deliberately the last thing
      // before letting them in: past the token exchange and past the admin
      // check. Setting it earlier would arm an automatic redirect for someone
      // refused at /api/auth/admin/me, repeating that refusal on every visit
      // with no way to reach the login page. A password sign-in never reaches
      // this line, which is what keeps the redirect off those users.
      rememberSsoUser(
        safeStorage(() => window.localStorage),
        safeStorage(() => window.sessionStorage),
      );

      navigate('/', { replace: true });
    };

    void run().catch(() => {
      setNotice('Single sign-on failed. Please try again or sign in another way.');
    });
  }, [navigate, checkAccess]);

  return (
    <AuthSplitLayout variant="admin">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h2 className="text-2xl text-slate-900">Signing you in</h2>
          <p className="text-sm text-slate-500">Completing single sign-on…</p>
        </div>
        {notice ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
              {notice}
            </div>
            <Link to="/login" className="text-sm font-medium text-brand-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <CenteredSpinner />
        )}
      </div>
    </AuthSplitLayout>
  );
}
